@file:NoLiveLiterals
@file:Suppress("unused")

package com.example.borshi.web.home

import androidx.compose.runtime.NoLiveLiterals

// ─────────────────────────────────────────────────────────────────────────────
// Domain Result wrapper — replaces bare nullable / try-catch at call sites
// ─────────────────────────────────────────────────────────────────────────────
@Suppress("unused")
sealed class Result<out T> {
    data class Success<T>(val data: T) : Result<T>()
    data class Error(val exception: AppException) : Result<Nothing>()
    object Loading : Result<Nothing>()

    // ── Helpers ───────────────────────────────────────────────────────────

    fun getOrNull(): T? = (this as? Success)?.data

    fun getOrElse(default: @UnsafeVariance T): T = (this as? Success)?.data ?: default

    fun <R> map(transform: (T) -> R): Result<R> = when (this) {
        is Success -> Success(transform(data))
        is Error   -> this
        is Loading -> Loading
    }

    fun onSuccess(block: (T) -> Unit): Result<T> {
        if (this is Success) block(data)
        return this
    }

    fun onError(block: (AppException) -> Unit): Result<T> {
        if (this is Error) block(exception)
        return this
    }

    val isSuccess: Boolean get() = this is Success
    val isError:   Boolean get() = this is Error
    val isLoading: Boolean get() = this is Loading
}

// ─────────────────────────────────────────────────────────────────────────────
// Typed exceptions — richer context than raw Throwable
// ─────────────────────────────────────────────────────────────────────────────
sealed class AppException(override val message: String, override val cause: Throwable? = null) : Exception(message, cause) {

    class NetworkError(
        message: String = "Network connection failed",
        cause: Throwable? = null
    ) : AppException(message, cause)

    class DataNotFound(
        message: String = "Requested data not found",
        cause: Throwable? = null
    ) : AppException(message, cause)

    class ValidationError(
        message: String,
        cause: Throwable? = null
    ) : AppException(message, cause)

    class AuthError(
        message: String = "Authentication required",
        cause: Throwable? = null
    ) : AppException(message, cause)

    class UnknownError(
        cause: Throwable,
        message: String = cause.message ?: "Unknown error"
    ) : AppException(message, cause)

    // Convenience: human-readable label for UI
    val userMessage: String get() = when (this) {
        is NetworkError    -> "Check your internet connection and try again."
        is DataNotFound    -> "Content could not be found. Please refresh."
        is ValidationError -> message
        is AuthError       -> "Please sign in to continue."
        is UnknownError    -> "Something went wrong. Please try again."
    }

    companion object {
        /** Maps a raw Throwable to the most appropriate AppException subtype. */
        fun from(e: Throwable): AppException {
            val msg = e.message ?: ""
            return when {
                msg.contains("network",    ignoreCase = true) ||
                msg.contains("fetch",      ignoreCase = true) ||
                msg.contains("timeout",    ignoreCase = true) -> NetworkError(cause = e)

                msg.contains("not found",  ignoreCase = true) ||
                msg.contains("no such",    ignoreCase = true) -> DataNotFound(cause = e)

                msg.contains("permission", ignoreCase = true) ||
                msg.contains("sign in",    ignoreCase = true) -> AuthError(cause = e)

                else -> UnknownError(e)
            }
        }
    }
}

